import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Briefcase, MessageCircle, MapPin, User, Settings,
  HelpCircle, LogOut, Menu, X, Bell, ChevronDown, Power, Award,
  DollarSign, Clock, FileText, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';
import NotificationBell from '@/components/NotificationBell';
import RoleSwitcher from '@/components/RoleSwitcher';

// Customer navigation items
const customerNavItems = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'My Jobs', href: '/jobs', icon: Briefcase },
  { name: 'Messages', href: '/messages', icon: MessageCircle },
  { name: 'My Addresses', href: '/addresses', icon: MapPin },
  { name: 'Profile', href: '/profile', icon: User },
  { name: 'Settings', href: '/settings', icon: Settings },
];

// Additional customer menu items (shown in dropdown only)
const customerDropdownOnlyItems = [
  { name: 'Become a Contractor', href: '/become-contractor', icon: Award, highlight: true },
];

// Contractor navigation items
const contractorNavItems = [
  { name: 'Dashboard', href: '/contractor/dashboard', icon: LayoutDashboard },
  { name: 'Live Jobs', href: '/contractor/live-jobs', icon: Power },
  { name: 'Job History', href: '/contractor/jobs', icon: Briefcase },
  { name: 'Messages', href: '/messages', icon: MessageCircle },
  { name: 'Profile', href: '/profile', icon: User },
  { name: 'Certifications', href: '/contractor/certifications', icon: Award },
  { name: 'Earnings', href: '/contractor/earnings', icon: DollarSign },
  { name: 'Settings', href: '/settings', icon: Settings },
];

// Mobile menu items
const customerMobileMenuItems = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Jobs', href: '/jobs', icon: Briefcase },
  { name: 'Messages', href: '/messages', icon: MessageCircle },
  { name: 'Profile', href: '/profile', icon: User },
];

const contractorMobileMenuItems = [
  { name: 'Dashboard', href: '/contractor/dashboard', icon: LayoutDashboard },
  { name: 'Live Jobs', href: '/contractor/live-jobs', icon: Power },
  { name: 'Messages', href: '/messages', icon: MessageCircle },
  { name: 'Profile', href: '/profile', icon: User },
];

const DashboardShell = ({ children }) => {
  const { user, logout, activeRole, hasAdminAccess, isContractor: isContractorMode } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // Use activeRole from auth context (supports role switching)
  const isContractor = isContractorMode || activeRole === 'contractor';
  const navItems = isContractor ? contractorNavItems : customerNavItems;
  const mobileItems = isContractor ? contractorMobileMenuItems : customerMobileMenuItems;

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Failed to log out');
    } finally {
      setLoggingOut(false);
    }
  };

  const isActivePath = (path) => {
    if (path === '/dashboard' || path === '/contractor/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Shared across all dashboard pages */}
      <header className="h-16 border-b border-zinc-200 bg-white/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-full flex items-center justify-between">
          {/* Logo */}
          <Link to={isContractor ? '/contractor/dashboard' : '/dashboard'} className="flex items-center gap-2">
            <span className="text-2xl font-bold tracking-tight text-zinc-900">ZOLT</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.slice(0, 5).map((item) => {
              const Icon = item.icon;
              const isActive = isActivePath(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive 
                      ? 'bg-zinc-900 text-white' 
                      : 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="w-4 h-4" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Right side - Role Switcher + Notifications + User Menu */}
          <div className="flex items-center gap-2">
            {/* Role Switcher */}
            <RoleSwitcher />
            
            {/* Notifications */}
            <NotificationBell />

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 px-3 py-2"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-zinc-600" />
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-zinc-700">
                    {user?.name?.split(' ')[0]}
                  </span>
                  <ChevronDown className="w-4 h-4 text-zinc-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  {user?.name}
                  <p className="text-xs text-zinc-500 font-normal">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {/* Admin Panel link for admin users */}
                {hasAdminAccess && (
                  <>
                    <DropdownMenuItem
                      onClick={() => navigate('/admin')}
                      className="cursor-pointer bg-purple-50"
                      data-testid="menu-admin-panel"
                    >
                      <Shield className="w-4 h-4 mr-2 text-purple-600" />
                      <span className="text-purple-700 font-medium">Admin Panel</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                
                {/* All menu items */}
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem
                      key={item.name}
                      onClick={() => navigate(item.href)}
                      className="cursor-pointer"
                      data-testid={`menu-${item.name.toLowerCase().replace(' ', '-')}`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {item.name}
                    </DropdownMenuItem>
                  );
                })}
                
                {/* Become a Contractor option for customers */}
                {!isContractor && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => {
                        sessionStorage.setItem('contractorOnboardingReturnTo', window.location.pathname);
                        navigate('/become-contractor');
                      }}
                      className="cursor-pointer bg-blue-50"
                      data-testid="menu-become-contractor"
                    >
                      <Award className="w-4 h-4 mr-2 text-blue-600" />
                      <span className="text-blue-700 font-medium">Become a Contractor</span>
                    </DropdownMenuItem>
                  </>
                )}
                
                <DropdownMenuItem
                  onClick={() => navigate('/help')}
                  className="cursor-pointer"
                  data-testid="menu-help"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Help & Support
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="text-red-600 cursor-pointer focus:text-red-600 focus:bg-red-50"
                  data-testid="menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {loggingOut ? 'Logging out...' : 'Log out'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-white z-10 border-t border-zinc-200">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActivePath(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    isActive 
                      ? 'bg-zinc-900 text-white' 
                      : 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.name}
                </Link>
              );
            })}
            <div className="border-t border-zinc-200 my-2 pt-2">
              <Link
                to="/help"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
              >
                <HelpCircle className="w-5 h-5" />
                Help & Support
              </Link>
              <button
                onClick={handleLogout}
                disabled={loggingOut}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-5 h-5" />
                {loggingOut ? 'Logging out...' : 'Log out'}
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 z-20">
        <div className="flex justify-around items-center h-16">
          {mobileItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center justify-center py-2 px-4 ${
                  isActive ? 'text-zinc-900' : 'text-zinc-500'
                }`}
                data-testid={`mobile-nav-${item.name.toLowerCase().replace(' ', '-')}`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-zinc-900' : 'text-zinc-400'}`} />
                <span className="text-xs mt-1 font-medium">{item.name}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Spacer for mobile bottom nav */}
      <div className="md:hidden h-16" />
    </div>
  );
};

export default DashboardShell;
